import React, { useRef, useCallback, useEffect } from "react";
import classNames from "classnames";
import { VariableSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import {
  BlueprintRunnerStepLogEntryType,
  BlueprintRunnerStepLogs,
} from "../../../models/Blueprints";
import LogRow from "./IntegrationEditorStepLogsTableRow";

// Types definition
interface Props {
  startTime: string;
  logs: BlueprintRunnerStepLogs;
}

interface RowMeasurerProps {
  log: BlueprintRunnerStepLogs[number];
  startTime: string;
  onHeight: (height: number) => void;
}

interface RowProps {
  index: number;
  style: React.CSSProperties;
  log: BlueprintRunnerStepLogs[number];
  startTime: string;
  onHeightChange: (index: number, height: number) => void;
}

/**
 * Visual indicator for log entry type (error, warning, info)
 */
const EventTypeRectangle = ({ event_type }: { event_type: BlueprintRunnerStepLogEntryType }) => {
  const colorClass =
    event_type === BlueprintRunnerStepLogEntryType.Error
      ? "console-error"
      : event_type === BlueprintRunnerStepLogEntryType.Warning
      ? "console-warning"
      : "console-info";
  const rectClasses = classNames("event-type-rectangle", colorClass);
  return <div className={rectClasses}></div>;
};

/**
 * Component that measures its own height after rendering
 * and reports it back through onHeight callback
 */
const RowMeasurer = React.forwardRef<HTMLDivElement, RowMeasurerProps>(
  ({ log, startTime, onHeight }, ref) => {
    const rowRef = useRef<HTMLDivElement>(null);
    const resolvedRef = (ref as React.RefObject<HTMLDivElement>) || rowRef;

    // Measure after render and when content changes
    useEffect(() => {
      if (resolvedRef.current) {
        // Get the actual rendered height (add 1px for the border)
        const height = resolvedRef.current.getBoundingClientRect().height + 1;
        onHeight(height);
      }
    }, [log, onHeight]);

    return (
      <div ref={resolvedRef} className="p-0">
        <LogRow log={log} startTime={startTime} EventTypeRectangle={EventTypeRectangle} />
        <hr className="my-1 border-t border-gray-10 w-full" />
      </div>
    );
  }
);

/**
 * Individual row component for the virtualized list
 */
const VirtualizedRow = ({ index, style, log, startTime, onHeightChange }: RowProps) => {
  const rowStyle: React.CSSProperties = {
    ...style,
    boxSizing: "border-box",
    padding: "0",
  };

  return (
    <div style={rowStyle}>
      <RowMeasurer
        log={log}
        startTime={startTime}
        onHeight={(height) => onHeightChange(index, height)}
      />
    </div>
  );
};

/**
 * Main virtualized log table component
 */
const IntegrationEditorStepLogsTableVirtualized = ({ startTime, logs }: Props) => {
  // Cache for row heights
  const heightCache = useRef<{ [key: number]: number }>({});
  const listRef = useRef<List>(null);
  const defaultHeight = 80;

  /**
   * Get row height from cache or use default height
   */
  const getItemSize = useCallback((index: number) => heightCache.current[index] || defaultHeight, [
    defaultHeight,
  ]);

  /**
   * Update height in cache and refresh list when height changes
   */
  const setItemHeight = useCallback((index: number, height: number) => {
    if (heightCache.current[index] !== height) {
      // Add a small buffer to account for potential measurement errors
      heightCache.current[index] = Math.ceil(height) + 4;

      if (listRef.current) {
        listRef.current.resetAfterIndex(index);
      }
    }
  }, []);

  /**
   * Row renderer function for react-window
   */
  const rowRenderer = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const log = logs[index];
      return (
        <VirtualizedRow
          index={index}
          style={style}
          log={log}
          startTime={startTime}
          onHeightChange={setItemHeight}
        />
      );
    },
    [logs, startTime, setItemHeight]
  );

  // Reset height cache when logs change
  useEffect(() => {
    heightCache.current = {};
    if (listRef.current) {
      listRef.current.resetAfterIndex(0);
    }
  }, [logs]);

  // Empty state when no logs
  if (!logs || logs.length === 0) {
    return <div className="w-full h-full p-4">No logs available</div>;
  }

  return (
    <AutoSizer>
      {({ height, width }: { height: number; width: number }) => {
        const listHeight = height > 0 ? height : 300;

        return (
          <List
            className="logs-list"
            height={listHeight}
            width={width}
            itemCount={logs.length}
            itemSize={getItemSize}
            ref={listRef}
            overscanCount={100}
          >
            {rowRenderer}
          </List>
        );
      }}
    </AutoSizer>
  );
};

export default IntegrationEditorStepLogsTableVirtualized;
